// Диалоги рисунков: свойства (ширина в см, замещающий текст, заимствование с указанием источника),
// обрезка, рисунок из интернета, изображения проекта.
import { useEffect, useRef, useState } from 'react';
import type { DialogProps } from '../../../core/registry';
import type { AssetInfo } from '../../../core/types';
import { assetUrl } from '../../../core/schema/objects';
import { useApp } from '../../../core/store';
import { Btn, Field, Modal } from '../../../ui/controls';
import { effectiveCm, fmtCm, naturalCm, TEXT_W_CM, widthToCm } from '../util';
import { assetInfo, insertAsset, insertFromUrl, transformImage } from './upload';
import { figureAt } from './common';
import type { PartImage } from './parts';

const PART_LETTERS = 'абвгдежиклмнпрстуфхцшщэюя';

// ------------------------------------------------------------------------------------------------ свойства
export function FigureDialog({ ctx, props, close }: DialogProps) {
  const f = figureAt(ctx);
  const sources = useApp((s) => s.sources);
  const a = f?.node.attrs || {};
  const info = a.src ? assetInfo(a.src) : null;
  const [width, setWidth] = useState<string>(() => {
    const cm = widthToCm(a.width);
    return cm ? String(Math.round(cm * 10) / 10).replace('.', ',') : '';
  });
  const [alt, setAlt] = useState<string>(a.alt || '');
  const [src, setSrc] = useState<string>(a.borrowed?.source || '');
  const [loc, setLoc] = useState<string>(a.borrowed?.loc || '');
  // составной рисунок: части «а», «б» … под одной подписью (attrs.images), раскладка layout/cols
  const [parts, setParts] = useState<PartImage[]>(() => (Array.isArray(a.images) && a.images.length > 1
    ? a.images.map((x: PartImage, k: number) => ({ ...x, src: k === 0 ? a.src || x.src || '' : x.src || '' })) : []));
  const [layout, setLayout] = useState<string>(() => (a.layout === 'grid' || a.cols ? `grid${a.cols || 2}` : a.layout || ''));
  const altRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (props.focus === 'alt') altRef.current?.focus(); }, []);
  if (!f) return <Modal title="Свойства рисунка" onClose={() => close()}><p>Выделите рисунок.</p></Modal>;
  const natCm = info ? Math.min(naturalCm(info.width, info.dpi ?? 96), TEXT_W_CM) : null;
  const cmNum = Number(width.replace(',', '.'));
  const cur = info ? effectiveCm(width ? `${cmNum}cm` : null, info.width, info.height, info.dpi) : null;
  const ok = () => {
    const g = figureAt(ctx);
    if (!g) return close();
    const w = width.trim() && cmNum > 0 ? `${Math.min(TEXT_W_CM, Math.round(cmNum * 10) / 10)}cm` : null;
    const borrowed = src ? { source: src, loc: loc.trim() } : null;
    let pa: Record<string, unknown> = {};
    if (parts.length > 1) {
      const grid = layout.startsWith('grid');
      pa = { images: parts.map((x) => ({ src: x.src || '', width: x.width ?? null, label: (x.label || '').trim() })), src: parts[0].src || '',
        width: parts[0].width ?? null, layout: grid ? 'grid' : layout || null, cols: grid ? Number(layout.slice(4)) || 2 : null, missing: null };
    } else if (parts.length === 1) {
      pa = { images: null, src: parts[0].src || '', width: parts[0].width ?? null, layout: null, cols: null };   // осталась одна часть
    }
    ctx.editor!.view.dispatch(ctx.editor!.state.tr.setNodeMarkup(g.pos, undefined, { ...g.node.attrs, width: w, alt: alt.trim(), borrowed, ...pa }));
    close(true);
  };
  return (
    <Modal title="Свойства рисунка" testid="tfc-figure-dialog" onClose={() => close()} width={560}
      footer={<><Btn label="Отмена" onClick={() => close()} /><Btn label="Готово" primary onClick={ok} testid="tfc-figure-ok" /></>}>
      <div className="form-grid" onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); ok(); } }}>
        {parts.length > 1 && (
          <>
            <Field label="Части рисунка (4.5.7)" wide hint="метки под частями; пояснение «а – …; б – …» над подписью - в Markdown строкой !expl">
              <div className="tfc-parts-edit" data-testid="tfc-fig-parts">
                {parts.map((x, k) => (
                  <span key={k} className="row">
                    <input value={x.label || ''} style={{ width: 56 }} data-testid="tfc-fig-part-label"
                      onChange={(e) => setParts(parts.map((y, i) => (i === k ? { ...y, label: e.target.value } : y)))} />
                    <span className="muted">{x.src ? x.src : 'нет изображения'}</span>
                    <Btn icon="close" title="Убрать часть" onClick={() => setParts(parts.filter((_, i) => i !== k))} />
                  </span>
                ))}
              </div>
            </Field>
            <Field label="Раскладка частей">
              <select value={layout} onChange={(e) => setLayout(e.target.value)} data-testid="tfc-fig-layout">
                <option value="">по размерам частей</option>
                <option value="row">в ряд</option>
                <option value="column">друг под другом</option>
                <option value="grid2">сетка по 2</option>
                <option value="grid3">сетка по 3</option>
              </select>
            </Field>
          </>
        )}
        <Field label="" wide>
          <Btn label="+ Часть рисунка (а, б …)" title="Несколько изображений под одной подписью - части «а», «б» (4.5.7); файл части - на рисунке «Выбрать файл…»"
            testid="tfc-fig-add-part" onClick={() => setParts(parts.length > 1
              ? [...parts, { src: '', width: null, label: PART_LETTERS[parts.length] || String(parts.length + 1) }]
              : [{ src: a.src || '', width: a.width ?? null, label: 'а' }, { src: '', width: null, label: 'б' }])} />
        </Field>
        {parts.length <= 1 && <><Field label="Ширина, см" hint={`пусто - исходный размер${natCm ? ` (${fmtCm(natCm)})` : ''}; не больше 17 см (поле набора)`}>
          <input value={width} onChange={(e) => setWidth(e.target.value)} placeholder={natCm ? String(Math.round(natCm * 10) / 10).replace('.', ',') : '15'} data-testid="tfc-fig-width" />
        </Field>
        <Field label="Быстро">
          <div className="row">
            {[['17', 'Поле'], ['12', '12'], ['8,5', '½'], ['', '1:1']].map(([v, t]) => <Btn key={t} label={t} onClick={() => setWidth(v)} />)}
          </div>
        </Field></>}
        {info && parts.length <= 1 && <div className="field-wide muted tfc-fig-meta">Файл: {info.width} × {info.height} пикс., {Math.round(info.dpi ?? 96)} dpi{cur ? ` → в отчёте ${fmtCm(cur)} × ${fmtCm((cur * info.height) / info.width)}, ≈${Math.round(info.width / (cur / 2.54))} dpi` : ''}</div>}
        <Field label="Замещающий текст (9.4.4)" wide hint="краткое описание изображения для незрячих; пусто - «Рисунок N. Название»">
          <input ref={altRef} value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Например: схема стенда из трёх узлов, соединённых коммутатором" data-testid="tfc-fig-alt" />
        </Field>
        <Field label="Рисунок заимствован из источника (4.5.2)" wide hint="в подписи появится ссылка [N, с. …]">
          <select value={src} onChange={(e) => setSrc(e.target.value)} data-testid="tfc-fig-source">
            <option value="">- свой рисунок -</option>
            {sources.map((s) => <option key={s.id} value={s.id}>{(s.formatted || s.text || s.title || s.id).slice(0, 90)}</option>)}
          </select>
        </Field>
        {src && <Field label="Страница"><input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="с. 45" /></Field>}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ обрезка
type Rect = { x: number; y: number; w: number; h: number };
export function CropDialog({ ctx, close }: DialogProps) {
  const f = figureAt(ctx);
  const [r, setR] = useState<Rect>({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  if (!f || !f.node.attrs.src) return <Modal title="Обрезка" onClose={() => close()}><p>Выделите рисунок с изображением.</p></Modal>;
  const drag = (e: React.PointerEvent, mode: string) => {
    e.preventDefault(); e.stopPropagation();
    const b = box.current!.getBoundingClientRect();
    const x0 = e.clientX; const y0 = e.clientY; const r0 = { ...r };
    const clamp = (v: number) => Math.max(0, Math.min(1, v));
    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - x0) / b.width; const dy = (ev.clientY - y0) / b.height;
      let { x, y, w, h } = r0;
      if (mode === 'move') { x = clamp(Math.min(1 - w, x + dx)); y = clamp(Math.min(1 - h, y + dy)); }
      if (mode.includes('l')) { const nx = clamp(Math.min(x + w - 0.02, x + dx)); w += x - nx; x = nx; }
      if (mode.includes('r')) w = Math.max(0.02, Math.min(1 - x, w + dx));
      if (mode.includes('t')) { const ny = clamp(Math.min(y + h - 0.02, y + dy)); h += y - ny; y = ny; }
      if (mode.includes('b')) h = Math.max(0.02, Math.min(1 - y, h + dy));
      setR({ x, y, w, h });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const ok = async () => {
    setBusy(true);
    try { await transformImage(ctx, f.node.attrs.id, { crop: r }); close(true); } catch (e: any) { ctx.toast(e.message || String(e), 'error'); setBusy(false); }
  };
  const pct = (v: number) => `${v * 100}%`;
  return (
    <Modal title="Обрезка изображения" testid="tfc-crop-dialog" onClose={() => close()} width={760}
      footer={<><span className="muted" style={{ marginRight: 'auto' }}>Исходный файл не меняется - можно отменить (Ctrl+Z)</span>
        <Btn label="Сбросить" onClick={() => setR({ x: 0, y: 0, w: 1, h: 1 })} /><Btn label="Отмена" onClick={() => close()} />
        <Btn label={busy ? 'Обрезка…' : 'Обрезать'} primary disabled={busy} onClick={ok} testid="tfc-crop-ok" /></>}>
      <div className="tfc-crop">
        <div className="tfc-crop-box" ref={box}>
          <img src={assetUrl(f.node.attrs.src)} alt="" draggable={false} />
          <div className="tfc-crop-shade" style={{ clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${pct(r.y)}, ${pct(r.x)} ${pct(r.y)}, ${pct(r.x)} ${pct(r.y + r.h)}, ${pct(r.x + r.w)} ${pct(r.y + r.h)}, ${pct(r.x + r.w)} ${pct(r.y)}, 0 ${pct(r.y)})` }} />
          <div className="tfc-crop-rect" style={{ left: pct(r.x), top: pct(r.y), width: pct(r.w), height: pct(r.h) }} onPointerDown={(e) => drag(e, 'move')}>
            {['tl', 't', 'tr', 'r', 'br', 'b', 'bl', 'l'].map((m) => <span key={m} className={'tfc-crop-h tfc-crop-' + m} onPointerDown={(e) => drag(e, m)} />)}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ из интернета
export function UrlDialog({ ctx, close }: DialogProps) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ok = async () => {
    setBusy(true); setErr('');
    try { await insertFromUrl(ctx, url.trim()); close(true); ctx.toast('Рисунок вставлен - введите название и укажите источник (4.5.2)', 'ok', 4000); } catch (e: any) { setErr(e.message || String(e)); setBusy(false); }
  };
  return (
    <Modal title="Рисунок из интернета" testid="tfc-url-dialog" onClose={() => close()} width={560}
      footer={<><Btn label="Отмена" onClick={() => close()} /><Btn label={busy ? 'Загрузка…' : 'Вставить'} primary disabled={busy || !/^https?:\/\/\S+/.test(url.trim())} onClick={ok} testid="tfc-url-ok" /></>}>
      <Field label="Адрес изображения (правый щелчок по картинке → «Копировать адрес изображения»)" wide>
        <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/picture.png" data-testid="tfc-url"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void ok(); } }} />
      </Field>
      {err && <p className="tfc-warn">{err}</p>}
      <p className="tfc-note">Заимствованные иллюстрации сопровождают ссылкой на источник (4.5.2): «Свойства рисунка» → «Рисунок заимствован».</p>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ изображения проекта
export function GalleryDialog({ ctx, close }: DialogProps) {
  const [list, setList] = useState<AssetInfo[] | null>(null);
  const used = new Set<string>();
  ctx.editor?.state.doc.descendants((n) => { if (n.type.name === 'figure' && n.attrs.src) used.add(n.attrs.src); return true; });
  useEffect(() => {
    const pid = ctx.store.getState().projectId;
    if (pid) ctx.api.listAssets(pid).then((r) => setList(r.assets)).catch(() => setList([]));
  }, []);
  return (
    <Modal title="Изображения проекта" testid="tfc-gallery" onClose={() => close()} width={760}>
      {!list ? <p className="muted">Загрузка…</p> : !list.length ? <p className="muted">В проекте пока нет изображений.</p> : (
        <div className="tfc-gallery">
          {list.map((a) => (
            <button key={a.name} type="button" className={'tfc-gal-item' + (used.has(a.name) ? ' is-used' : '')}
              title={used.has(a.name) ? 'Уже используется в документе (повтор иллюстраций не допускается, 4.5.2)' : 'Вставить рисунок'}
              onClick={() => { close(true); setTimeout(() => insertAsset(ctx, a.name), 0); }}>
              <img src={assetUrl(a.name, 256)} alt="" loading="lazy" />
              <span>{a.width} × {a.height}{used.has(a.name) ? ' · в тексте' : ''}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
